// ============================================================================
// SETTLEMENT TYPES (PMS Phase 2 slice 3a)
// ============================================================================
// Who pays for what on a stay. A default on each party master (agent, corporate,
// OTA). A booking follows its party master unless a mode is chosen for the stay
// (2026-10-16): read when each charge posts.

export type SettlementMode = 'GUEST' | 'CREDIT_BOOKED' | 'CREDIT_FULL';

/** "As party master" — not a mode: the stay follows its party's default (stored as no mode). */
export const FOLLOW_PARTY = 'PARTY' as const;
/** What the booking form and Change Settlement choose: a mode, or follow the party master. */
export type SettlementChoice = SettlementMode | typeof FOLLOW_PARTY;

export const SETTLEMENT_MODE_OPTIONS: Array<{ value: SettlementMode; label: string; hint: string; credit: boolean }> = [
  { value: 'GUEST', label: 'Guest pays', hint: 'The guest settles everything at checkout.', credit: false },
  {
    value: 'CREDIT_BOOKED',
    label: 'Credit — booking only',
    hint: 'This party is billed for the room, meal plan and booked add-ons; the guest settles restaurant, laundry and other extras.',
    credit: true,
  },
  { value: 'CREDIT_FULL', label: 'Credit — full stay', hint: 'This party is billed for everything, extras included.', credit: true },
];

export const settlementModeLabel = (v?: string | null): string =>
  v === FOLLOW_PARTY ? 'As party master'
    : SETTLEMENT_MODE_OPTIONS.find((o) => o.value === v)?.label ?? (v || '');

/** How far an in-house settlement change reaches (slice 3b). */
export type SettlementApplyTo = 'WHOLE_STAY' | 'FROM_NOW';

/** PUT /Booking/{id}/settlement */
export interface ChangeSettlementRequest {
  /** A mode, or 'PARTY' to follow the party master. */
  settlementMode: SettlementChoice;
  applyTo: SettlementApplyTo;
  reason: string;
}

/** GET /Booking/{id}/settlement/preview?mode= — what the change would move. */
export interface SettlementChangePreview {
  /** The stay follows its party master now (2026-10-16). */
  currentFollowsParty?: boolean;
  /** The party master's settlement default (null without a party). */
  partyDefaultMode?: SettlementMode | null;
  bookingId: string;
  currentMode: SettlementMode;
  newMode: SettlementMode;
  partyType: 'AGENT' | 'CORPORATE' | 'OTA' | null;
  partyName: string | null;
  partyIsCreditEligible: boolean;
  linesToParty: number;
  amountToParty: number;
  linesToGuest: number;
  amountToGuest: number;
  linesLocked: number;
}

/** The tax-identity and settlement fields every party master carries. */
export interface PartySettlementFields {
  /** Name the GSTIN is registered to; empty = the master's own name. */
  gstLegalName?: string | null;
  /** Statutory state code: from the GSTIN when registered, the address state otherwise (read-only). */
  taxStateCode?: string | null;
  defaultSettlementMode?: SettlementMode;
  showRatesOnGuestStatement?: boolean;
  /** Early departure for this party's bookings: FULL / NONE / ASK; null = the property default (slice 7c-2). */
  earlyDepartureRetention?: string | null;
  /** GST registered (slice 3a-ii): a GSTIN is held and the tax state comes from it. */
  isGstRegistered?: boolean;
  /** Tax pack supply type for invoices to this party (STANDARD, SEZ_WITHOUT_TAX, …). */
  defaultSupplyType?: string;
}
